import React, { useState, useRef, useEffect } from 'react'
import { ComplementsAPI } from '../api'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { HelpCircle, Image as ImageIcon } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { RichEditor } from './RichEditor'
import { MediaManager } from './MediaManager'
import { useAdmin } from '../lib/hooks'
import { PaypalReturnUrl } from './PaypalReturnUrl'

const Field = ({ label, required, tooltip, error, children }: { label: string; required?: boolean; tooltip?: string; error?: string; children: React.ReactNode }) => (
    <div className="space-y-1">
        <div className="flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            <span>{label}</span>
            {required ? <span className="text-red-600">*</span> : null}
            {tooltip ? <span title={tooltip}><HelpCircle className="w-4 h-4 text-gray-400" /></span> : null}
        </div>
        {children}
        {error ? <div className="text-xs text-red-600">{error}</div> : null}
    </div>
)

const AdminNewComplement = () => {
    const { isAdmin, loading: adminLoading } = useAdmin()
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()

    const [form, setForm] = useState<any>(() => {
        if (location.state?.cloneData) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, createdAt, updatedAt, ...rest } = location.state.cloneData
            return {
                ...rest,
                name: `${rest.name}-copy`,
                displayName: `${rest.displayName} (Copia)`,
                currency: rest.currency || 'USD',
                price: rest.price || 0,
                payType: rest.payType || 'ANUAL',
                payTypeDisplay: rest.payTypeDisplay || '',
                description: rest.description || '',
                paypalButtonHtml: rest.paypalButtonHtml || '',
                sortOrder: rest.sortOrder || 0,
                isVisible: rest.isVisible !== undefined ? rest.isVisible : true,
            }
        }
        return {
            name: '',
            displayName: '',
            mainPhotoUrl: '',
            currency: 'USD',
            price: 0,
            payType: 'ANUAL',
            payTypeDisplay: '',
            description: '',
            paypalButtonHtml: '',
            sortOrder: 0,
            isVisible: true,
        }
    })

    // Load data if editing
    useEffect(() => {
        if (id) {
            setSaving(true)
            ComplementsAPI.get(id).then(res => {
                if (res.success && res.data) {
                    setForm(res.data)
                }
            }).finally(() => setSaving(false))
        }
    }, [id])

    const [saving, setSaving] = useState(false)
    const [message, setMessage] = useState<string | null>(null)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [showMediaManager, setShowMediaManager] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const { getToken } = useAuth()

    if (adminLoading) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Cargando permisos...</p></div>
    if (!isAdmin) return <div className="min-h-[300px] flex items-center justify-center"><p className="text-gray-600">Acceso restringido</p></div>

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type, checked } = e.target as any
        setForm((prev: any) => ({ ...prev, [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value }))
    }


    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSaving(true)
        setMessage(null)
        try {
            const ok = validate()
            if (!ok) {
                setSaving(false)
                return
            }

            let res
            if (id) {
                res = await ComplementsAPI.update(id, form)
            } else {
                res = await ComplementsAPI.create(form)
            }

            if (res.success) {
                setMessage(id ? 'Complemento actualizado' : 'Complemento creado')
                navigate('/admin/complements')
            } else {
                setMessage(res.error || 'Error')
            }
        } catch (err: any) {
            console.error('Error creating/updating complement:', err) // Log full DB error
            const errorMsg = err?.message || 'Error desconocido'
            const errorDetail = err?.detail || err?.hint || ''
            setMessage(`${errorMsg} ${errorDetail ? `(${errorDetail})` : ''}`)
        } finally {
            setSaving(false)
        }
    }

    const errorClass = (name: string) => (errors[name] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '')

    const validate = () => {
        const e: Record<string, string> = {}
        if (!form.name) e.name = 'Requerido'
        if (!form.displayName) e.displayName = 'Requerido'
        if (!form.currency) e.currency = 'Requerido'
        if (form.price === undefined || form.price === null || Number(form.price) < 0) e.price = 'Inválido'
        if (!form.payType) e.payType = 'Requerido'
        setErrors(e)
        return Object.keys(e).length === 0
    }

    const onPickFeatured = () => {
        fileInputRef.current?.click()
    }
    const onUploadFeatured = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0]
        if (!f) return
        const token = await getToken()
        const slugBase = (form.displayName || form.name || 'complement').toString()
        const slug = slugBase.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
        const res = await fetch(`/api/upload-featured?slug=${encodeURIComponent(slug)}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token || ''}`, 'Content-Type': f.type },
            body: f,
        })
        const data = await res.json()
        if (res.ok && data?.url) {
            setForm((prev: any) => ({ ...prev, mainPhotoUrl: data.url }))
            setMessage('Imagen destacada subida')
        } else {
            setMessage(data?.error || 'Error al subir imagen')
        }
    }


    return (
        <div className="max-w-7xl mx-auto p-6 mt-24">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold">{id ? 'Editar Complemento' : 'Nuevo Complemento'}</h2>
                <button onClick={() => navigate('/admin/complements')} className="px-3 py-2 rounded-md bg-gray-200 hover:bg-gray-300 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-white">Volver</button>
            </div>
            <form onSubmit={onSubmit} className="space-y-8">
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm dark:bg-slate-800 dark:border-slate-700">
                    <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700">
                        <h3 className="text-lg font-semibold dark:text-white">Datos</h3>
                    </div>
                    <div className="p-5">
                        <div className="grid grid-cols-12 gap-6">
                            {/* Left Column: Image */}
                            <div className="col-span-12 md:col-span-4 lg:col-span-3">
                                <Field label="Imagen destacada" tooltip="Imagen destacada del complemento">
                                    <div className="space-y-3">
                                        <div className="w-full aspect-square bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden relative dark:bg-slate-900 dark:border-slate-700">
                                            {form.mainPhotoUrl ? (
                                                <img src={form.mainPhotoUrl} alt="Preview" className="w-full h-full object-contain p-4" />
                                            ) : (
                                                <ImageIcon className="w-16 h-16 text-gray-300 dark:text-gray-600" />
                                            )}
                                        </div>
                                        <div className="flex gap-2">
                                            <input className="border rounded-md px-3 py-2 w-full text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white" name="mainPhotoUrl" value={form.mainPhotoUrl} onChange={onChange} placeholder="https://..." />
                                            <button type="button" onClick={onPickFeatured} className="px-3 py-2 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium text-sm whitespace-nowrap dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50">Subir</button>
                                        </div>
                                        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onUploadFeatured} />
                                    </div>
                                </Field>
                            </div>

                            {/* Right Column: Fields */}
                            <div className="col-span-12 md:col-span-8 lg:col-span-9 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Field label="Orden" required tooltip="Orden de aparición (menor primero)" error={errors.sortOrder}>
                                    <input className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('sortOrder')}`} type="number" name="sortOrder" value={form.sortOrder} onChange={onChange} placeholder="0" />
                                </Field>
                                <Field label="Visibilidad" required tooltip="Estado público">
                                    <select
                                        className="border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                                        name="isVisible"
                                        value={form.isVisible ? 'true' : 'false'}
                                        onChange={(e) => setForm((prev: any) => ({ ...prev, isVisible: e.target.value === 'true' }))}
                                    >
                                        <option value="true">Visible</option>
                                        <option value="false">Oculto</option>
                                    </select>
                                </Field>

                                <Field label="Nombre interno" required tooltip="Identificador sin espacios" error={errors.name}>
                                    <input className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('name')}`} name="name" value={form.name} onChange={onChange} placeholder="Identificador interno" />
                                </Field>
                                <Field label="Nombre para mostrar" required tooltip="Título visible" error={errors.displayName}>
                                    <input className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('displayName')}`} name="displayName" value={form.displayName} onChange={onChange} placeholder="Ej: Certificado SSL" />
                                </Field>
                                <Field label="Moneda" required tooltip="Moneda de precio" error={errors.currency}>
                                    <select className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('currency')}`} name="currency" value={form.currency} onChange={onChange}>
                                        <option value="USD">USD</option>
                                        <option value="UYU">UYU</option>
                                    </select>
                                </Field>
                                <Field label="Precio" required tooltip="Monto en la moneda seleccionada" error={errors.price}>
                                    <input className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('price')}`} type="number" name="price" value={form.price} onChange={onChange} placeholder="0" />
                                </Field>
                                <Field label="Tipo de pago" required tooltip="Periodicidad" error={errors.payType}>
                                    <select className={`border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white ${errorClass('payType')}`} name="payType" value={form.payType} onChange={onChange}>
                                        <option value="ANUAL">Anual</option>
                                        <option value="MENSUAL">Mensual</option>
                                        <option value="UNICO">Único</option>
                                        <option value="REFERIDO">Referido</option>
                                    </select>
                                </Field>
                                <Field label="Etiqueta de pago" tooltip="Texto corto como 'por mes'">
                                    <input className="border rounded-md px-3 py-2 w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white" name="payTypeDisplay" value={form.payTypeDisplay} onChange={onChange} placeholder="por mes, por año" />
                                </Field>

                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 mt-6">
                            <div className="space-y-1">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Descripción</div>
                                <RichEditor
                                    value={form.description}
                                    onChange={(val) => setForm((p: any) => ({ ...p, description: val }))}
                                    placeholder="Descripción detallada del complemento"
                                />
                            </div>

                            <Field label="Botón Paypal HTML" tooltip="Pega el HTML del botón">
                                <textarea className="border rounded-md px-3 py-2 w-full font-mono text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white" rows={4} name="paypalButtonHtml" value={form.paypalButtonHtml} onChange={onChange} placeholder="<form>...</form>" />
                                <PaypalReturnUrl planName={form.name} />
                            </Field>
                        </div>

                    </div>
                </div>


                <div className="flex items-center gap-3">
                    <button disabled={saving} className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{saving ? 'Guardando...' : 'Guardar complemento'}</button>
                    <button type="button" onClick={() => navigate('/admin/complements')} className="px-3 py-2 rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-slate-700 dark:text-gray-300 dark:hover:bg-slate-700">Cancelar</button>
                    {message && <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>}
                </div>
            </form>
            {showMediaManager && <MediaManager onClose={() => setShowMediaManager(false)} />}
        </div>
    )
}

export default AdminNewComplement
